/* ================================================================
   AiAssistantPage.tsx — Module Assistant IA (Ollama local)
   Vue santé • Insights • Journal d'erreurs • Élèves à risque • Chat
   ================================================================ */
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Sparkles, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Info,
  ShieldAlert, GraduationCap, Wallet, Banknote, CalendarDays,
  Activity, FileWarning, Send, Loader2, MessageSquare, Bot, User,
  TrendingDown, BarChart3, Clock, Wrench
} from 'lucide-react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'

/* ─── Types ─── */
interface Insight {
  id: number
  insight_type: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  description?: string
  recommendation?: string
  data?: any
  source: 'rule' | 'ai'
  status: string
  created_at?: string
}
interface Health {
  ollama_online: boolean
  model: string
  models_installed: string[]
  insights_active: number
  insights_critical: number
  errors_24h: number
  errors_7d: number
}
interface ErrorLog {
  id: number; request_id?: string; method?: string; path?: string
  error_type?: string; error_message?: string; created_at?: string
}
interface ChatMsg { role: 'user' | 'assistant'; text: string; source?: string }

const TYPE_META: Record<string, { label: string; icon: any; color: string }> = {
  error:        { label: 'Erreurs',         icon: FileWarning,  color: 'text-red-600' },
  security:     { label: 'Sécurité',        icon: ShieldAlert,  color: 'text-purple-600' },
  student_risk: { label: 'Élèves à risque', icon: GraduationCap,color: 'text-rose-600' },
  class_drop:   { label: 'Chutes de niveau',icon: TrendingDown, color: 'text-orange-600' },
  budget:       { label: 'Budget',          icon: Wallet,       color: 'text-emerald-600' },
  finance:      { label: 'Scolarité',       icon: Banknote,     color: 'text-teal-600' },
  planning:     { label: 'Planification',   icon: CalendarDays, color: 'text-blue-600' },
  performance:  { label: 'Performance',     icon: BarChart3,    color: 'text-cyan-600' },
}

const SEV_META: Record<string, { badge: string; icon: any }> = {
  critical: { badge: 'bg-red-100 text-red-700 border-red-200',       icon: XCircle },
  warning:  { badge: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  info:     { badge: 'bg-blue-100 text-blue-700 border-blue-200',    icon: Info },
}

type Tab = 'insights' | 'errors' | 'risk' | 'chat'

export default function AiAssistantPage() {
  const { user } = useAuthStore()
  const { lang } = useLang()

  const [health, setHealth] = useState<Health | null>(null)
  const [insights, setInsights] = useState<Insight[]>([])
  const [totalInsights, setTotalInsights] = useState(0)
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([])
  const [riskStudents, setRiskStudents] = useState<any[]>([])
  const [tab, setTab] = useState<Tab>('insights')
  const [typeFilter, setTypeFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [healthLoading, setHealthLoading] = useState(true)

  /* ─── Chat ─── */
  const [messages, setMessages] = useState<ChatMsg[]>([
    { role: 'assistant', text: lang === 'fr'
      ? "Bonjour ! Je suis l'assistant IA de My Helper School. Posez-moi une question sur l'utilisation du logiciel (connexion, notes, budget, discipline…)."
      : "Hello! I'm the My Helper School AI assistant. Ask me anything about using the software." }
  ])
  const [question, setQuestion] = useState('')
  const [chatLoading, setChatLoading] = useState(false)

  /* ─── Chargements ─── */
  const loadHealth = useCallback(async () => {
    setHealthLoading(true)
    try {
      const res: any = await apiService.get('/ai/health')
      setHealth(res?.data ?? res)
    } catch { /* silencieux */ } finally { setHealthLoading(false) }
  }, [])

  const loadInsights = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { status: 'active', per_page: 100 }
      if (typeFilter) params.insight_type = typeFilter
      const res: any = await apiService.get('/ai/insights', { params })
      const body = res?.data ?? res
      setInsights(body?.items || [])
      setTotalInsights(body?.total || 0)
    } catch { /* silencieux */ } finally { setLoading(false) }
  }, [typeFilter])

  const loadErrors = useCallback(async () => {
    try {
      const res: any = await apiService.get('/ai/error-logs', { params: { per_page: 100 } })
      setErrorLogs((res?.data ?? res)?.items || [])
    } catch { /* silencieux */ }
  }, [])

  const loadRisk = useCallback(async () => {
    try {
      const res: any = await apiService.get('/ai/students-at-risk')
      setRiskStudents((res?.data ?? res)?.students || [])
    } catch { /* silencieux */ }
  }, [])

  useEffect(() => {
    loadHealth(); loadInsights(); loadErrors(); loadRisk()
  }, [loadHealth, loadInsights, loadErrors, loadRisk])

  const runAnalysis = async () => {
    setAnalyzing(true)
    try {
      const res: any = await apiService.post('/ai/analyze', {})
      const body = res?.data ?? res
      const c = body?.by_severity || {}
      toast.success(
        `Analyse terminée : ${body?.created ?? 0} nouvel(s) insight(s)` +
        (c.critical ? ` — ${c.critical} critique(s) ⚠️` : '')
      )
      await Promise.all([loadInsights(), loadHealth(), loadRisk()])
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'analyse")
    } finally { setAnalyzing(false) }
  }

  const resolve = async (id: number, action: 'resolve' | 'dismiss') => {
    try {
      await apiService.post(`/ai/insights/${id}/${action}`, {})
      toast.success(action === 'resolve' ? 'Marqué comme résolu' : 'Ignoré')
      loadInsights(); loadHealth()
    } catch { toast.error('Erreur') }
  }

  const ask = async () => {
    const q = question.trim()
    if (!q || chatLoading) return
    setMessages(m => [...m, { role: 'user', text: q }])
    setQuestion('')
    setChatLoading(true)
    try {
      const res: any = await apiService.post('/ai/ask', { question: q, page: window.location.pathname })
      const body = res?.data ?? res
      setMessages(m => [...m, { role: 'assistant', text: body?.answer || '…', source: body?.source }])
    } catch {
      setMessages(m => [...m, { role: 'assistant', text: lang === 'fr' ? "Erreur de communication avec l'assistant." : 'Communication error.' }])
    } finally { setChatLoading(false) }
  }

  const typeCounts = useMemo(() => {
    const m: Record<string, number> = {}
    insights.forEach(i => { m[i.insight_type] = (m[i.insight_type] || 0) + 1 })
    return m
  }, [insights])

  const TABS = [
    { id: 'insights' as Tab, label: lang === 'fr' ? 'Insights' : 'Insights', icon: Sparkles, count: totalInsights },
    { id: 'errors' as Tab, label: lang === 'fr' ? 'Erreurs' : 'Errors', icon: FileWarning, count: health?.errors_7d },
    { id: 'risk' as Tab, label: lang === 'fr' ? 'Élèves à risque' : 'At-risk students', icon: GraduationCap, count: riskStudents.length },
    { id: 'chat' as Tab, label: lang === 'fr' ? 'Assistant' : 'Assistant', icon: MessageSquare },
  ]

  return (
    <div className="min-h-screen bg-gray-50 p-6 max-w-7xl mx-auto space-y-6">

      {/* ═══ HEADER ═══ */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-violet-600" />
            {lang === 'fr' ? 'Assistant IA' : 'AI Assistant'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {lang === 'fr'
              ? 'Analyse locale des erreurs, données et dysfonctionnements — 100% sur votre VPS.'
              : 'Local analysis of errors, data and malfunctions — 100% on your VPS.'}
          </p>
        </div>
        <button onClick={runAnalysis} disabled={analyzing}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl text-sm font-semibold hover:from-violet-700 hover:to-purple-700 transition-all shadow-md shadow-violet-200 disabled:opacity-60">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {analyzing ? (lang === 'fr' ? 'Analyse en cours…' : 'Analyzing…') : (lang === 'fr' ? '🔍 Analyser maintenant' : '🔍 Analyze now')}
        </button>
      </div>

      {/* ═══ BANDEAU SANTÉ ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${health?.ollama_online ? 'bg-emerald-100' : 'bg-red-100'}`}>
            <Bot className={`w-5 h-5 ${health?.ollama_online ? 'text-emerald-600' : 'text-red-600'}`} />
          </div>
          <div>
            <p className="text-xs text-gray-500">Ollama</p>
            <p className={`text-sm font-bold ${health?.ollama_online ? 'text-emerald-600' : 'text-red-600'}`}>
              {healthLoading ? '…' : health?.ollama_online ? 'En ligne' : 'Hors ligne'}
            </p>
            {health?.ollama_online && <p className="text-[10px] text-gray-400">{health.model}</p>}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <XCircle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{lang === 'fr' ? 'Erreurs 24h / 7j' : 'Errors 24h / 7d'}</p>
            <p className="text-lg font-bold text-gray-900">{healthLoading ? '—' : `${health?.errors_24h ?? 0} / ${health?.errors_7d ?? 0}`}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{lang === 'fr' ? 'Alertes actives' : 'Active alerts'}</p>
            <p className="text-lg font-bold text-gray-900">{healthLoading ? '—' : health?.insights_active ?? 0}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
            <Activity className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{lang === 'fr' ? 'Mode' : 'Mode'}</p>
            <p className="text-sm font-bold text-gray-900">{health?.ollama_online ? 'IA + règles' : 'Règles (fallback)'}</p>
          </div>
        </div>
      </div>

      {!health?.ollama_online && !healthLoading && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-sm text-amber-800">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Ollama est hors ligne — le module fonctionne en mode « règles ».</p>
            <p className="mt-1">Les détections et recommandations continuent de fonctionner, mais sans enrichissement IA. Sur le VPS : <code className="bg-amber-100 px-1 rounded">systemctl status ollama</code> puis <code className="bg-amber-100 px-1 rounded">bash backend/scripts/setup_ollama.sh</code>.</p>
          </div>
        </div>
      )}

      {/* ═══ ONGLETS ═══ */}
      <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-200 p-1 w-fit flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
            <t.icon className="w-4 h-4" />{t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ═══ ONGLET : INSIGHTS ═══ */}
      {tab === 'insights' && (
        <div className="space-y-4">
          {/* Filtres par type */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setTypeFilter('')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${!typeFilter ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {lang === 'fr' ? 'Tous' : 'All'} ({insights.length})
            </button>
            {Object.entries(TYPE_META).map(([key, meta]) => (
              typeCounts[key] ? (
                <button key={key} onClick={() => setTypeFilter(typeFilter === key ? '' : key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${typeFilter === key ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  <meta.icon className="w-3.5 h-3.5" />{meta.label} ({typeCounts[key]})
                </button>
              ) : null
            ))}
          </div>

          {loading ? (
            <div className="py-16 text-center"><Loader2 className="w-8 h-8 text-violet-600 animate-spin mx-auto" /></div>
          ) : insights.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="font-semibold text-gray-900">{lang === 'fr' ? 'Aucune alerte active 🎉' : 'No active alerts 🎉'}</p>
              <p className="text-sm text-gray-500 mt-1">{lang === 'fr' ? "Cliquez sur « Analyser maintenant » pour lancer une analyse complète." : 'Click "Analyze now" to run a full analysis.'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {insights.map(i => {
                const sev = SEV_META[i.severity] || SEV_META.info
                const type = TYPE_META[i.insight_type] || { label: i.insight_type, icon: Sparkles, color: 'text-gray-600' }
                return (
                  <div key={i.id} className={`bg-white rounded-xl border-l-4 p-5 shadow-sm ${
                    i.severity === 'critical' ? 'border-l-red-500' : i.severity === 'warning' ? 'border-l-amber-500' : 'border-l-blue-400'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-gray-50 flex-shrink-0`}>
                        <sev.icon className={`w-5 h-5 ${i.severity === 'critical' ? 'text-red-600' : i.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${sev.badge}`}>{i.severity}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 flex items-center gap-1`}>
                            <type.icon className={`w-3 h-3 ${type.color}`} />{type.label}
                          </span>
                          {i.source === 'ai' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-100 text-violet-700">🤖 IA</span>
                          )}
                          <span className="text-[10px] text-gray-400 ml-auto flex items-center gap-1">
                            <Clock className="w-3 h-3" />{i.created_at ? new Date(i.created_at).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US') : ''}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{i.title}</h3>
                        {i.description && <p className="text-sm text-gray-600 mt-1">{i.description}</p>}
                        {i.recommendation && (
                          <div className="mt-2 bg-violet-50 border border-violet-100 rounded-lg p-3 text-sm text-violet-900">
                            <span className="font-semibold">💡 {lang === 'fr' ? 'Recommandation : ' : 'Recommendation: '}</span>{i.recommendation}
                          </div>
                        )}
                        {i.data?.students && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {i.data.students.slice(0, 8).map((s: any) => (
                              <span key={s.student_id} className="px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-100 rounded-full text-xs font-medium">
                                {s.name} ({s.avg}/20{s.unjustified_absences ? ` • ${s.unjustified_absences} abs.` : ''})
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          <button onClick={() => resolve(i.id, 'resolve')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors">
                            <CheckCircle2 className="w-3.5 h-3.5" />{lang === 'fr' ? 'Marquer résolu' : 'Mark resolved'}
                          </button>
                          <button onClick={() => resolve(i.id, 'dismiss')}
                            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors">
                            {lang === 'fr' ? 'Ignorer' : 'Dismiss'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ ONGLET : ERREURS ═══ */}
      {tab === 'errors' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">{lang === 'fr' ? 'Journal technique des erreurs (500)' : 'Server error log (500)'}</h3>
            <button onClick={loadErrors} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><RefreshCw className="w-4 h-4" /></button>
          </div>
          {errorLogs.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">{lang === 'fr' ? 'Aucune erreur enregistrée 🎉' : 'No errors recorded 🎉'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Endpoint</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {errorLogs.map(e => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US') : '—'}</td>
                      <td className="px-4 py-2"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-red-700">{e.method} {e.path}</code></td>
                      <td className="px-4 py-2 text-xs font-medium text-gray-700">{e.error_type}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 max-w-md truncate" title={e.error_message}>{e.error_message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ ONGLET : ÉLÈVES À RISQUE ═══ */}
      {tab === 'risk' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">{lang === 'fr' ? 'Élèves en difficulté scolaire (moyenne < 8/20 + absences)' : 'At-risk students (avg < 8/20 + absences)'}</h3>
          </div>
          {riskStudents.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">{lang === 'fr' ? 'Aucun élève détecté. Lancez une analyse.' : 'No student detected. Run an analysis.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">{lang === 'fr' ? 'Élève' : 'Student'}</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Matricule</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">{lang === 'fr' ? 'Moyenne' : 'Average'}</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">{lang === 'fr' ? 'Abs. non just.' : 'Unjust. abs.'}</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">{lang === 'fr' ? 'Niveau de risque' : 'Risk level'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {riskStudents.map((s, idx) => {
                    const risk = s.avg < 6 ? 'critical' : 'high'
                    return (
                      <tr key={s.student_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-xs text-gray-400">{idx + 1}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{s.name}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-blue-600">{s.matricule}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-red-600">{s.avg}/20</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{s.unjustified_absences || 0}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${risk === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {risk === 'critical' ? (lang === 'fr' ? 'CRITIQUE' : 'CRITICAL') : (lang === 'fr' ? 'ÉLEVÉ' : 'HIGH')}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ ONGLET : CHAT ═══ */}
      {tab === 'chat' && (
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col" style={{ height: '60vh' }}>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Bot className="w-5 h-5 text-violet-600" />
            <h3 className="text-sm font-semibold text-gray-700">{lang === 'fr' ? 'Assistant conversationnel' : 'Conversational assistant'}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${health?.ollama_online ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {health?.ollama_online ? 'IA locale' : 'FAQ seule'}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-violet-600" />
                  </div>
                )}
                <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm ${m.role === 'user' ? 'bg-violet-600 text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
                  {m.text}
                  {m.source && <span className="block mt-1 text-[9px] opacity-60">source : {m.source}</span>}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            {chatLoading && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-violet-100 rounded-full flex items-center justify-center"><Bot className="w-4 h-4 text-violet-600" /></div>
                <div className="bg-gray-100 px-4 py-2.5 rounded-2xl rounded-bl-sm"><Loader2 className="w-4 h-4 text-violet-600 animate-spin" /></div>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-gray-100 flex gap-2">
            <input value={question} onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && ask()}
              placeholder={lang === 'fr' ? 'Posez votre question… (ex: comment saisir les notes ?)' : 'Ask your question…'}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30" />
            <button onClick={ask} disabled={chatLoading || !question.trim()}
              className="px-4 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}